import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { CurrentUserService } from '@services/auth/current-user.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { MIN_PASSWORD_LENGTH, ValidationService } from '@services/ui/validation.service';
import { UserService } from '@services/user/user.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { SharedModule } from '@shared/modules/shared.module';
import { translateApiError } from 'app/core/utils/api-error';

/**
 * Own password change (PUT /users/me/password).
 * - `dialog`: modal content with its own header and a success screen (mobile profile sheet).
 * - `inline`: fields inside the profile's security card (desktop profile); success is a toast and
 *   `closeDialog` tells the profile to hide the fields.
 */
@Component({
	selector: 'app-change-password',
	imports: [SharedModule, ButtonComponent, InputComponent],
	templateUrl: './change-password.component.html',
	styleUrl: './change-password.component.scss',
})
export class ChangePasswordComponent {
	@Input() variant: 'dialog' | 'inline' = 'dialog';
	@Output() closeDialog = new EventEmitter<void>();

	/** Inline: the host adds no box, the fields lay out inside the profile card as before */
	@HostBinding('class.inline') get isInline(): boolean {
		return this.variant === 'inline';
	}

	passwordForm: FormGroup;
	success = false;
	isLoading = false;

	constructor(
		private fb: FormBuilder,
		private validationService: ValidationService,
		private userService: UserService,
		private messageAlertService: MessageAlertService,
		private currentUserService: CurrentUserService,
		private translocoService: TranslocoService,
	) {
		this.passwordForm = this.fb.group(
			{
				currentPassword: ['', [Validators.required]],
				newPassword: ['', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)]],
				confirmPassword: [
					'',
					[Validators.required, this.validationService.confirmPasswordValidator('newPassword')],
				],
			},
			{
				// Also re-checks the confirmation when the new password changes after it
				validators: this.matchPasswordsValidator(),
			},
		);
	}

	private matchPasswordsValidator(): ValidatorFn {
		return (group: AbstractControl): ValidationErrors | null => {
			const newPassword = group.get('newPassword')?.value;
			const confirmPassword = group.get('confirmPassword')?.value;

			if (confirmPassword && newPassword !== confirmPassword) {
				group.get('confirmPassword')?.setErrors({ passwordsDoNotMatch: true });
				return { passwordsDoNotMatch: true };
			}

			return null;
		};
	}

	close(): void {
		this.closeDialog.emit();
	}

	onSubmit(): void {
		if (this.isLoading) {
			return;
		}
		if (this.passwordForm.invalid) {
			this.passwordForm.markAllAsTouched();
			return;
		}

		const { currentPassword, newPassword } = this.passwordForm.value;
		const userId = this.currentUserService.getUserId();

		if (!userId) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('profile.messages.userNotFound'),
			);
			return;
		}

		this.isLoading = true;
		// Own endpoint: the admin one (/users/:id/password) needs users:update, so clients got a 403
		this.userService.updateOwnPassword({ currentPassword, password: newPassword }).subscribe({
			next: () => {
				this.isLoading = false;
				this.passwordForm.reset();
				if (this.variant === 'inline') {
					this.messageAlertService.showMessage(
						'success',
						this.translocoService.translate('common.labels.success'),
						this.translocoService.translate('profile.messages.passwordUpdated'),
					);
					this.closeDialog.emit();
				} else {
					this.success = true;
				}
			},
			error: (error: unknown) => {
				this.isLoading = false;
				const fallbackKey =
					this.variant === 'inline'
						? 'profile.messages.errorChangingPassword'
						: 'authMessages.passwordUpdateError';
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					// errors.<code> (e.g. wrong current password), generic key otherwise
					translateApiError(error, this.translocoService, fallbackKey),
				);
			},
		});
	}

	getErrorMessage(field: string): string | null {
		const control = this.passwordForm?.get(field);
		if (this.variant === 'inline') {
			if (!control || !control.errors || !control.touched) return null;
			const errorKey = Object.keys(control.errors)[0];
			// With the validator's params ({{requiredLength}} for minlength)
			return errorKey
				? this.validationService.getValidatorErrorMessage(errorKey, control.errors[errorKey])
				: null;
		}
		if (control?.errors) {
			if (control.errors['required']) {
				return this.translocoService.translate('common.messages.requiredField');
			}
			if (control.errors['invalidPassword']) {
				return this.validationService.getValidatorErrorMessage('invalidPassword');
			}
			if (control.errors['passwordsDoNotMatch']) {
				return this.translocoService.translate('auth.errors.passwordsDoNotMatch');
			}
			if (control.errors['minlength']) {
				return this.validationService.getValidatorErrorMessage('minlength', control.errors['minlength']);
			}
		}
		return null;
	}
}
